import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { CONTENT } from '../content';
import { useTokenomics, TokenomicsData } from '../useTokenomics';
import { C, EASE_OUT, Lettering, hover } from '../kit';

/**
 * Tokenomics for design D, in three layouts to compare (`?tok=a|b|c`, local review only).
 * Every fact has its own Zeus illustration; every figure is live text in the title
 * lettering, never baked into an image; the supply is an ink pie chart fed by the API.
 * While the API is down the pie stays grey and says so, never shows made up numbers.
 */

const [lp, contract, tax, supply] = CONTENT.tokenomics.cards;

export const FACTS = [
  { key: 'supply', big: '420.69T', label: 'Fixed supply', note: '420 690 000 000 000 $ZEUS, and never one more', link: supply.link, art: '/redesign2/tokenomics/supply.webp' },
  { key: 'tax', big: '0%', label: 'Tax', note: 'No tax on buys or sells', link: tax.link, art: '/redesign2/tokenomics/tax.webp' },
  { key: 'locked', big: 'LOCKED', label: 'Contract', note: contract.text, link: contract.link, art: '/redesign2/tokenomics/locked.webp' },
  { key: 'burned', big: 'BURNED', label: 'LP renounced', note: lp.text, link: lp.link, art: '/redesign2/tokenomics/burned.webp' },
];

const LP_PAIR_URL = 'https://etherscan.io/address/0xf97503af8230a7e72909d6614f45e88168ff3c10';

const compact = (n: string) => {
  const v = Number(n);
  return v >= 1e12 ? `${(v / 1e12).toFixed(2)}T` : v >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : v.toLocaleString('en-US');
};
const usd = (v: number) => (v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(1)}K` : `$${v.toFixed(0)}`);

/* ----------------------------------------------------------------- pie chart */

const slice = (cx: number, cy: number, r: number, from: number, to: number) => {
  const a0 = ((from - 90) * Math.PI) / 180;
  const a1 = ((to - 90) * Math.PI) / 180;
  const large = to - from > 180 ? 1 : 0;
  return `M${cx} ${cy} L${cx + r * Math.cos(a0)} ${cy + r * Math.sin(a0)} A${r} ${r} 0 ${large} 1 ${cx + r * Math.cos(a1)} ${cy + r * Math.sin(a1)} Z`;
};

const Pie: React.FC<{ data: TokenomicsData | null; size?: number; down?: boolean }> = ({ data, size = 320, down = false }) => {
  const lpDeg = data ? (data.lpZeusPct / 100) * 360 : 0;
  const span = Math.max(lpDeg, 4);
  const from = down ? 180 - span / 2 : 0;
  return (
    <PieSvg
      data-chart="pie"
      viewBox="0 0 220 220"
      width={size}
      height={size}
      role="img"
      aria-label={data ? `LP locked ${data.lpZeusPct.toFixed(2)} percent, circulating ${data.circulatingPct.toFixed(2)} percent` : 'Supply chart loading'}
    >
      <circle cx="110" cy="110" r="100" fill={data ? C.white : '#dde6f2'} stroke={C.ink} strokeWidth="5" />
      {data && (
        <g className="lp">
          <path d={slice(110, 110, 100, from, from + span)} fill={C.yellow} stroke={C.ink} strokeWidth="5" strokeLinejoin="round" />
        </g>
      )}
      {!down && <circle cx="110" cy="110" r="5" fill={C.ink} />}
    </PieSvg>
  );
};

const pop = keyframes`
  from { transform: scale(0.6) rotate(-40deg); opacity: 0; }
  to { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const PieSvg = styled.svg`
  display: block;
  max-width: 100%;
  height: auto;
  overflow: visible;
  animation: ${pop} 700ms ${EASE_OUT} both;
  .lp {
    transform-origin: 110px 110px;
    transition: transform 260ms ${EASE_OUT};
  }
  ${hover(css`
    &:hover .lp {
      transform: translate(6px, -8px) scale(1.06);
    }
  `)}
`;

const Legend: React.FC<{ data: TokenomicsData | null; failed: boolean; align?: 'left' | 'center' }> = ({ data, failed, align = 'left' }) => (
  <LegendBox $align={align}>
    <p className="status">
      {data
        ? 'Live onchain data'
        : failed
        ? 'Onchain data unavailable right now, try again in a minute'
        : 'reading the chain...'}
    </p>
    <div className="row">
      <span className="sw" style={{ background: C.yellow }} />
      <div>
        <strong>LP locked forever</strong>
        {data && (
          <em>
            {data.lpZeusPct.toFixed(2)}% · {compact(data.lpZeus)} ZEUS{data.lpUsd != null ? ` · ${usd(data.lpUsd)}` : ''}
          </em>
        )}
        <small>Uniswap V2 pool, LP tokens burned to 0xdead</small>
      </div>
    </div>
    <div className="row">
      <span className="sw" style={{ background: C.white }} />
      <div>
        <strong>Circulating</strong>
        {data && (
          <em>
            {data.circulatingPct.toFixed(2)}% · {compact(data.circulatingZeus)} ZEUS
          </em>
        )}
      </div>
    </div>
    <p className="foot">
      Every trade on Uniswap V2 pays a 0.3% fee that stays inside the pool, and the LP tokens were burned, so this
      liquidity can never be pulled.{' '}
      <a href={LP_PAIR_URL} target="_blank" rel="noopener noreferrer">
        See the pool
      </a>
    </p>
  </LegendBox>
);

const LegendBox = styled.div<{ $align: 'left' | 'center' }>`
  display: grid;
  gap: 14px;
  max-width: 440px;
  text-align: ${(p) => p.$align};
  .status {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
  }
  .row {
    display: flex;
    gap: 12px;
    text-align: left;
  }
  .row > div {
    display: grid;
    gap: 2px;
  }
  .sw {
    flex: none;
    width: 22px;
    height: 22px;
    margin-top: 3px;
    border: 3px solid ${C.ink};
    border-radius: 6px;
  }
  strong {
    font-family: 'GROBOLD', sans-serif;
    font-weight: 400;
    font-size: 22px;
  }
  em {
    font-style: normal;
    font-weight: 700;
    font-size: 17px;
  }
  small {
    font-size: 15px;
  }
  .foot {
    margin: 0;
    font-size: 15.5px;
    line-height: 1.55;
  }
  .foot a {
    color: inherit;
    font-weight: 700;
  }
`;

/* ------------------------------------------------------------ fact pieces */

const Fact: React.FC<{ f: (typeof FACTS)[number]; size?: string; tilt?: number }> = ({ f, size = 'clamp(28px, min(3.2vw, 5vh), 52px)', tilt = 0 }) => (
  <a className="fact" href={f.link} target="_blank" rel="noopener noreferrer">
    <img className="art" src={f.art} alt="" aria-hidden="true" loading="lazy" />
    <Lettering $size={size} $tilt={tilt} className="big">
      {f.big}
    </Lettering>
    <span className="label">{f.label}</span>
    <span className="note">{f.note}</span>
    <span className="proof">Check on Etherscan</span>
  </a>
);

const factStyles = css`
  .fact {
    position: relative;
    display: grid;
    justify-items: center;
    gap: 4px;
    color: ${C.ink};
    text-align: center;
    text-decoration: none;
  }
  .art {
    width: clamp(90px, min(15vw, 14vh), 210px);
    height: auto;
    transition: transform 300ms ${EASE_OUT};
  }
  .big {
    margin-top: -14px;
    white-space: nowrap;
  }
  .label {
    margin-top: 6px;
    font-family: 'GROBOLD', sans-serif;
    font-size: clamp(17px, 1.5vw, 21px);
    text-transform: uppercase;
  }
  .note {
    max-width: 24ch;
    font-size: clamp(13.5px, 1.8vh, 15.5px);
    line-height: 1.4;
  }
  .proof {
    margin-top: 4px;
    font-size: 14px;
    font-weight: 700;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
  ${hover(css`
    .fact:hover .art {
      transform: translateY(-8px) rotate(-4deg) scale(1.05);
    }
  `)}
  .fact:focus-visible {
    outline: 3px dashed ${C.ink};
    outline-offset: 6px;
  }
`;

/* -------------------------------------------------- A: row of four, pie below */

const LayoutA: React.FC = () => {
  const { data, failed } = useTokenomics();
  return (
    <A>
      <div className="facts">
        {FACTS.map((f, i) => (
          <Fact key={f.key} f={f} tilt={[-2, 1.5, -1, 2][i]} />
        ))}
      </div>
      <div className="supply">
        <div className="pie">
          <h3>
            <Lettering $size="clamp(24px, 2.6vw, 36px)" $tilt={-1.5}>
              WHERE'S THE SUPPLY, ANON?
            </Lettering>
          </h3>
          <Pie data={data} size={300} />
        </div>
        <Legend data={data} failed={failed} />
      </div>
    </A>
  );
};

const A = styled.div`
  ${factStyles}
  .facts {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: clamp(16px, 3vw, 40px);
    max-width: 1180px;
    margin: 0 auto;
  }
  .supply {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 30px 70px;
    margin-top: clamp(50px, 6vw, 80px);
  }
  .pie {
    display: grid;
    justify-items: center;
    gap: 14px;
  }
  h3 {
    margin: 0;
  }
  @media (max-width: 900px) {
    .facts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;

/**
 * The pie with its figures written on it, like hand labels: the locked slice gets a
 * callout with a drawn leader line, the circulating share sits inside the white part,
 * and the pool note runs right under the pie.
 */
const PieLabeled: React.FC<{ data: TokenomicsData | null; failed: boolean }> = ({ data, failed }) => (
  <Labeled>
    <div className="stage">
      <Pie data={data} size={340} down />
      <div className="tag lp">
        <span className="sw" style={{ background: C.yellow }} />
        <strong>LP locked forever</strong>
        {data ? (
          <em>
            {data.lpZeusPct.toFixed(2)}% · {compact(data.lpZeus)} ZEUS{data.lpUsd != null ? ` · ${usd(data.lpUsd)}` : ''}
          </em>
        ) : null}
        <small>LP tokens burned to 0xdead</small>
      </div>
      <div className="tag circ">
        <strong>Circulating</strong>
        {data ? (
          <em>
            {data.circulatingPct.toFixed(2)}%<br />
            {compact(data.circulatingZeus)} ZEUS
          </em>
        ) : (
          <em>{failed ? 'Data unavailable right now' : 'reading the chain...'}</em>
        )}
      </div>
    </div>
    <p className="foot">
      Every trade on Uniswap V2 pays a 0.3% fee that stays inside the pool, and the LP tokens were burned, so this
      liquidity can never be pulled.{' '}
      <a href={LP_PAIR_URL} target="_blank" rel="noopener noreferrer">
        See the pool
      </a>
    </p>
  </Labeled>
);

const Labeled = styled.div`
  display: grid;
  justify-items: center;
  gap: 10px;
  .stage {
    position: relative;
    display: grid;
    justify-items: center;
  }
  .tag {
    display: grid;
    gap: 1px;
    color: ${C.ink};
    text-align: center;
  }
  .tag strong {
    font-family: 'GROBOLD', sans-serif;
    font-weight: 400;
    font-size: 20px;
  }
  .tag em {
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
  }
  .tag small {
    font-size: 14px;
  }
  .circ {
    position: absolute;
    left: 50%;
    top: 25%;
    transform: translate(-50%, -50%);
  }
  .circ strong {
    font-size: 26px;
  }
  .circ em {
    font-size: 18px;
  }
  .lp {
    position: relative;
    margin-top: 6px;
    padding-top: 22px;
  }
  .lp::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    height: 24px;
    border-left: 3px dashed ${C.ink};
  }
  .lp strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .lp .sw {
    display: none;
  }
  .lp strong::before {
    content: '';
    width: 16px;
    height: 16px;
    background: ${C.yellow};
    border: 3px solid ${C.ink};
    border-radius: 5px;
  }
  .foot {
    max-width: 44ch;
    margin: 6px 0 0;
    font-size: 15px;
    line-height: 1.5;
    text-align: center;
  }
  .foot a {
    color: inherit;
    font-weight: 700;
  }
`;

/* ------------------------------------------- B: pie in the middle, facts around */

const LayoutB: React.FC = () => {
  const { data, failed } = useTokenomics();
  return (
    <B>
      <div className="ring">
        {FACTS.slice(0, 2).map((f, i) => (
          <div key={f.key} className={`slot s${i}`}>
            <Fact f={f} tilt={i ? 2 : -2} />
          </div>
        ))}
        <div className="core">
          <h3>
            <Lettering $size="clamp(22px, 2.3vw, 32px)" $tilt={-1.5}>
              WHERE'S THE SUPPLY, ANON?
            </Lettering>
          </h3>
          <PieLabeled data={data} failed={failed} />
        </div>
        {FACTS.slice(2).map((f, i) => (
          <div key={f.key} className={`slot s${i + 2}`}>
            <Fact f={f} tilt={i ? 2 : -2} />
          </div>
        ))}
        <svg className="arrows" viewBox="0 0 1000 600" preserveAspectRatio="none" aria-hidden="true">
          <path d="M270 150 C 340 170, 360 200, 400 230" />
          <path d="M730 150 C 660 170, 640 200, 600 230" />
          <path d="M270 450 C 340 430, 360 400, 400 370" />
          <path d="M730 450 C 660 430, 640 400, 600 370" />
        </svg>
      </div>
    </B>
  );
};

const B = styled.div`
  ${factStyles}
  .ring {
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-template-rows: auto auto;
    grid-template-areas: 's0 center s1' 's2 center s3';
    align-items: center;
    gap: 10px 40px;
    max-width: 1180px;
    margin: 0 auto;
  }
  .s0 { grid-area: s0; }
  .s1 { grid-area: s1; }
  .s2 { grid-area: s2; }
  .s3 { grid-area: s3; }
  .core svg {
    width: min(340px, 34vh);
    height: auto;
  }
  .core {
    grid-area: center;
    display: grid;
    justify-items: center;
    min-width: min(420px, 90vw);
    text-align: center;
    gap: 14px;
    position: relative;
    z-index: 1;
  }
  h3 {
    margin: 0;
  }
  .arrows {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  .arrows path {
    fill: none;
    stroke: ${C.ink};
    stroke-width: 3;
    stroke-dasharray: 2 10;
    stroke-linecap: round;
  }
  .legend {
    display: flex;
    justify-content: center;
    margin-top: 40px;
  }
  .legend > div {
    max-width: 640px;
  }
  @media (max-width: 900px) {
    .ring {
      grid-template-columns: 1fr 1fr;
      grid-template-areas: 'center center' 's0 s1' 's2 s3';
    }
    .arrows {
      display: none;
    }
  }
`;

/* ----------------------------- C: Zeus walks the facts like a road, pie at the end */

const LayoutC: React.FC = () => {
  const { data, failed } = useTokenomics();
  return (
    <Cc>
      <ol className="road">
        {FACTS.map((f, i) => (
          <li key={f.key} className={i % 2 ? 'right' : 'left'}>
            <img className="art" src={f.art} alt="" aria-hidden="true" loading="lazy" />
            <a className="copy" href={f.link} target="_blank" rel="noopener noreferrer">
              <Lettering $size="clamp(40px, 4.6vw, 70px)" $tilt={i % 2 ? 2 : -2} className="big">
                {f.big}
              </Lettering>
              <span className="label">{f.label}</span>
              <span className="note">{f.note}</span>
              <span className="proof">Check on Etherscan</span>
            </a>
          </li>
        ))}
      </ol>
      <div className="supply">
        <Pie data={data} size={340} />
        <div>
          <h3>
            <Lettering $size="clamp(24px, 2.6vw, 36px)" $tilt={-1.5}>
              WHERE'S THE SUPPLY, ANON?
            </Lettering>
          </h3>
          <Legend data={data} failed={failed} />
        </div>
      </div>
    </Cc>
  );
};

const Cc = styled.div`
  ${factStyles}
  .road {
    display: grid;
    gap: clamp(10px, 2vw, 24px);
    max-width: 980px;
    margin: 0 auto;
    padding: 0;
    list-style: none;
  }
  .road li {
    display: flex;
    align-items: center;
    gap: clamp(20px, 4vw, 60px);
  }
  .road li.right {
    flex-direction: row-reverse;
    text-align: right;
  }
  .road .art {
    flex: none;
    width: clamp(160px, 20vw, 260px);
  }
  .copy {
    display: grid;
    gap: 4px;
    color: ${C.ink};
    text-decoration: none;
  }
  .road li.right .copy {
    justify-items: end;
  }
  .road .big {
    margin-top: 0;
  }
  ${hover(css`
    .road li:hover .art {
      transform: translateY(-8px) rotate(-4deg) scale(1.05);
    }
  `)}
  .supply {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 30px 60px;
    margin-top: clamp(50px, 6vw, 80px);
  }
  h3 {
    margin: 0 0 14px;
  }
  @media (max-width: 700px) {
    .road li,
    .road li.right {
      flex-direction: column;
      text-align: center;
    }
    .road li.right .copy {
      justify-items: center;
    }
  }
`;

/* ---------------------------------------------------------------------- pick */

type Variant = 'a' | 'b' | 'c';

const pickVariant = (): Variant => {
  if (typeof window === 'undefined') return 'b';
  const v = new URLSearchParams(window.location.search).get('tok');
  return v === 'a' || v === 'c' ? v : 'b';
};

export const TokenomicsD: React.FC = () => {
  const v = pickVariant();
  return v === 'b' ? <LayoutB /> : v === 'c' ? <LayoutC /> : <LayoutA />;
};
